// SimplifiedImportComponents.js – MUI 4 + Gatsby 4 compatible (v3‑2025‑06‑02)
// ----------------------------------------------------------------------------------
//  • Works with **@material‑ui/core 4.x** and **@material‑ui/data-grid 4.x**
//  • No MUI 5 (@mui/*) imports.
//  • Drop‑in for Gatsby 4 projects already using MUI 4.
// ----------------------------------------------------------------------------------

import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Chip,
  Tooltip,
  Typography,
  LinearProgress,
  IconButton,
  Button,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/ErrorOutline";
import WarningIcon from "@mui/icons-material/Warning";
import GTranslateIcon from "@mui/icons-material/GTranslate";
import { DataGrid } from "@mui/x-data-grid";
import { gql, useApolloClient } from "@apollo/client";
import { debugLog } from "../../utils"; // adjust path if necessary

const ALLOWED_FIELDS = {
  /* core entries */
  product: ["userTitle"],
  chapter: ["userTitle"],
  articleLongform: ["userTitle"],
  articleWithSteps: ["userTitle"],
  articleLongformComposableEntries: ["userTitle"],
  articleStepByStepComposableEntries: ["userTitle"],

  /* matrix / content-builder blocks */
  textBlock: ["paragraph"],
  textPlusImageBlock: ["paragraph"],
  richTextInlineImagesBlock: ["richText"],
  calloutBlock: ["calloutText", "calloutType"],
  videoBlock: ["paragraph", "youtubeVideo"],
  columnComposableBlock: [], // no additional fields

  /* other entry types */
  glossaryTerm: ["definition", "title"],
  variable: ["plainText"],
};

// -----------------------------------------------------------------------------
// Helpers – status → colour/icon mapping
// -----------------------------------------------------------------------------
const statusMeta = {
  modified: {
    label: "Modified",
    color: "primary",
    icon: <ArrowForwardIcon fontSize="small" />,
  },
  orphan: {
    label: "Create",
    color: "secondary",
    icon: <WarningIcon fontSize="small" />,
  },
  empty: {
    label: "Empty!",
    color: "default",
    icon: <WarningIcon fontSize="small" />,
  },
  typeErr: {
    label: "Type mismatch",
    color: "secondary",
    icon: <ErrorIcon fontSize="small" color="error" />,
  },
  uploaded: {
    label: "Imported",
    color: "primary",
    icon: <CheckCircleIcon fontSize="small" style={{ color: "green" }} />,
  },
  error: {
    label: "Error",
    color: "secondary",
    icon: <ErrorIcon fontSize="small" color="error" />,
  },
};

const rowStatus = r => {
  if (r.submitError) return "error";
  if (r.submitted) return "uploaded";
  if (r.typeMismatch) return "typeErr";
  if (r.emptyAfterChange) return "empty";
  if (!r.existsInCraft) return "orphan";
  if (r.modified) return "modified";
  return "";
};

// -----------------------------------------------------------------------------
// 1) SimplifiedTranslationDataGrid – list of blocks to be changed
// -----------------------------------------------------------------------------
export const SimplifiedTranslationDataGrid = React.memo(
  function SimplifiedTranslationDataGrid({ rows }) {
    /* ------------------------------------------------------------------
      Show every row the user has selected - even if it was marked
      “unchanged” - plus our usual auto-detected rows, but never show
      rows that are marked invalid (dup, type mismatch, step wrapper…). */
    // filter returns *new* array refs; cache the result so it only
    // recalculates when the source rows array itself changes.
    const displayRows = useMemo(
      () =>
        rows.filter(
          r =>
            (r.selected && !r.invalid) ||
            (r.submitted && !r.invalid) ||
            (r.submitError && !r.invalid)
        ),
      [rows]
    );

    const columns = useMemo(
      () => [
        { field: "entryType", headerName: "Type", width: 140 },
        { field: "entryId", headerName: "ID", width: 100 },
        {
          field: "entryUserTitle",
          headerName: "Title",
          width: 220,
          valueGetter: p =>
            p.row.entryUserTitle || p.row.updatedEntry?.userTitle || "",
        },
        {
          field: "entryContent",
          headerName: "Content",
          width: 300,
          valueGetter: p => {
            /* Highest-priority: the new entryContent helper column
              (present for variables, glossary definitions, paragraph blocks, …) */
            if (p.row.entryContent !== undefined) return p.row.entryContent;

            /* Fallbacks for legacy rows so nothing disappears */
            return (
              p.row.updatedEntry?.paragraph ??
              p.row.updatedEntry?.definition ?? // glossaryTerm
              p.row.updatedEntry?.plainText ?? // variable
              p.row.updatedEntry?.calloutText ??
              ""
            );
          },
        },
        {
          field: "status",
          headerName: "Status",
          width: 160,
          renderCell: p => {
            const s = rowStatus(p.row);
            if (!s) return null;
            const meta = statusMeta[s];
            return (
              <Chip
                icon={meta.icon}
                label={meta.label}
                color={meta.color}
                size="small"
                variant="outlined"
              />
            );
          },
        },
        {
          field: "submitError",
          headerName: "Error",
          width: 220,
          renderCell: p =>
            p.row.submitError ? (
              <Tooltip title={p.row.submitError} arrow>
                <Typography variant="caption" color="error">
                  {p.row.submitError.slice(0, 40)}
                </Typography>
              </Tooltip>
            ) : null,
        },
      ],
      []
    );

    if (!displayRows.length) {
      return (
        <Typography variant="body2" color="textSecondary">
          No changes detected.
        </Typography>
      );
    }

    return (
      <>
        <DataGrid
          rows={displayRows}
          columns={columns}
          autoHeight
          disableColumnMenu
          pageSize={Math.min(displayRows.length, 15)}
        />
        <Box mt={2} />
      </>
    );
  }
);

SimplifiedTranslationDataGrid.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
};

// -----------------------------------------------------------------------------
// 2) SimplifiedSubmitButton – batched on-the-fly mutations (WITH PROGRESS)
// -----------------------------------------------------------------------------
export function SimplifiedSubmitButton({ rows, siteId, onUpdate }) {
  const client = useApolloClient();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(0); // how many processed so far
  const [okCnt, setOk] = useState(0); // succeeded
  const [errCnt, setErr] = useState(0); // failed

  const todo = rows.filter(r => r.selected && !r.submitted && !r.typeMismatch)
    .length; // total to run

  const buildGql = (mutationName, vars) => {
    const defs = Object.entries(vars).map(([k, v]) => {
      const typ =
        k === "id"
          ? "ID"
          : Array.isArray(v)
          ? "[Int]"
          : k === "siteId"
          ? "Int"
          : k === "translationImportTimestamp"
          ? "DateTime"
          : "String";
      return `$${k}: ${typ}`;
    });

    const args = Object.keys(vars).map(k => `${k}: $${k}`);

    return gql`
      mutation ${mutationName}(${defs.join(", ")}) {
        ${mutationName}(${args.join(", ")}) { id }
      }
    `;
  };

  const run = async () => {
    setBusy(true);
    setDone(0);
    setOk(0);
    setErr(0);

    // Work on a shallow copy so we stay immutable
    const newRows = [...rows];

    for (let i = 0; i < newRows.length; i++) {
      const r = newRows[i];
      if (!r.selected || r.submitted || r.typeMismatch) continue;

      const blockType = (r.entryTypeHandle || r.entryType).replace(
        /_Entry$/,
        ""
      );
      const mutationName = r.fieldHandle
        ? `save_${r.fieldHandle}Field_${blockType}_Entry`
        : `save_${r.sectionHandle}_${blockType}_Entry`;

      const vars = {
        id: r.entryId,
        siteId: Number(siteId),
        translationImportTimestamp:
          r.exportTimestamp || new Date().toISOString(),
      };

      const allow = ALLOWED_FIELDS[blockType] || [];

      /* userTitle ---------------------------------------------------- */
      if (allow.includes("userTitle") && r.entryUserTitle !== undefined) {
        vars.userTitle = r.entryUserTitle;
      }
      /* glossaryTerm uses title not userTitle */
      if (blockType === "glossaryTerm" && allow.includes("title")) {
        vars.title = r.entryUserTitle;
      }

      /* paragraph / calloutText / definition / plainText / richText - */
      if (r.entryContent !== undefined) {
        if (blockType === "calloutBlock" && allow.includes("calloutText")) {
          vars.calloutText = r.entryContent;
        } else if (
          blockType === "glossaryTerm" &&
          allow.includes("definition")
        ) {
          vars.definition = r.entryContent;
        } else if (blockType === "variable" && allow.includes("plainText")) {
          vars.plainText = r.entryContent;
        } else if (allow.includes("richText")) {
          vars.richText = r.entryContent;
        } else if (allow.includes("paragraph")) {
          vars.paragraph = r.entryContent;
        }
      }

      /* calloutType -------------------------------------------------- */
      if (allow.includes("calloutType") && r.calloutType) {
        vars.calloutType = r.calloutType;
      }

      /* youtubeVideo ------------------------------------------------- */
      if (allow.includes("youtubeVideo") && r.youtubeVideo) {
        vars.youtubeVideo = r.youtubeVideo;
      }

      /* glossaryTerm ------------------------------------------------- */

      try {
        await client.mutate({
          mutation: buildGql(mutationName, vars),
          variables: vars,
        });
        newRows[i] = { ...r, submitted: true, submitError: "" };
        setOk(c => c + 1);
      } catch (e) {
        newRows[i] = {
          ...r,
          submitted: false,
          submitError:
            e.graphQLErrors?.[0]?.message ||
            e.networkError?.message ||
            e.message ||
            "Unknown error",
        };
        setErr(c => c + 1);
      }
      setDone(c => c + 1); // advance progress
    }

    setBusy(false);
    if (typeof onUpdate === "function") onUpdate(newRows);
  };

  /* -------------------------------- UI ------------------------------------ */

  const buttonLabel = busy
    ? `Submitting… ${done}/${todo}`
    : todo > 0
    ? `Submit ${todo} change${todo === 1 ? "" : "s"}`
    : "No changes selected";

  return (
    <Box mt={2} display="flex" alignItems="center" columnGap={16}>
      <Button
        variant="contained"
        color={errCnt ? "secondary" : "primary"}
        disabled={busy || todo === 0}
        onClick={run}
        startIcon={<ArrowForwardIcon />}
      >
        {buttonLabel}
      </Button>

      {/* progress bar while running */}
      {busy && <LinearProgress style={{ flex: 1 }} />}

      {/* final tally */}
      {!busy && (okCnt > 0 || errCnt > 0) && (
        <Typography variant="body2">
          {okCnt} succeeded&nbsp;&nbsp;•&nbsp;&nbsp;{errCnt} failed
        </Typography>
      )}
    </Box>
  );
}

// -----------------------------------------------------------------------------
// 3) SimplifiedDiffContent – glue component
// -----------------------------------------------------------------------------
export default function SimplifiedDiffContent({ rows, setRows, siteId }) {
  const handleUpdate = updated => setRows(updated);

  return (
    <Box mt={6}>
      <Typography variant="h6" gutterBottom>
        Blocks to be updated
      </Typography>

      <SimplifiedTranslationDataGrid rows={rows} />

      <Box mt={1} mb={2} p={1} style={{ background: "#f0f8ff" }}>
        <Typography variant="body2" style={{ fontWeight: 500 }}>
          Legend: <span style={{ color: "blue" }}>Blue</span> = modified,{" "}
          <span style={{ color: "Purple" }}>Purple</span> = create,
          <span style={{ color: "Orange" }}>Orange</span> = empty,{" "}
          <span style={{ color: "Green" }}>Green</span> = imported.
        </Typography>
      </Box>

      <SimplifiedSubmitButton
        rows={rows}
        siteId={siteId}
        onUpdate={handleUpdate}
      />
    </Box>
  );
}

SimplifiedDiffContent.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  setRows: PropTypes.func.isRequired,
  siteId: PropTypes.number.isRequired,
};
